'use client';

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import Spinner from './Spinner';
import { cn } from '~/utils';

interface LoadingOverlayProps extends VariantProps<typeof loadingOverlayVariants> {
    /** Whether to show the loading overlay */
    isLoading?: boolean;
    /** Custom text to display next to the spinner */
    text?: string;
    /** Custom className for the overlay container */
    className?: string;
    /** Custom className for the loading indicator */
    indicatorClassName?: string;
    /** Whether to show the overlay as a full-screen overlay */
    fullScreen?: boolean;
    /** Whether to make the loading indicator sticky positioned */
    sticky?: boolean;
    /** Custom spinner icon */
    spinner?: React.ReactNode;
}

const loadingOverlayVariants = cva(
    'flex flex-row items-center justify-center gap-3 rounded-3xl text-sm font-medium select-none',
    {
        variants: {
            variant: {
                default: 'bg-white',
            },
            size: {
                sm: 'px-3 py-2 text-xs',
                md: 'px-4 py-3 text-sm',
                lg: 'px-6 py-4 text-base',
            },
            shadow: {
                none: 'shadow-none',
                sm: 'shadow-xs',
                md: 'shadow-md',
                lg: 'shadow-lg',
                xl: 'shadow-xl',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'md',
            shadow: 'lg',
        },
    },
);

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
    isLoading = false,
    text,
    variant = 'default',
    size = 'md',
    shadow = 'lg',
    sticky = false,
    className,
    indicatorClassName,
    spinner,
}) => {
    const defaultSpinner = <Spinner className="size-4" />;

    const loadingIndicator = (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
                loadingOverlayVariants({ variant, size, shadow }),
                sticky && 'sticky top-1/2',
                indicatorClassName,
            )}
        >
            {spinner || defaultSpinner}
            {text && <span className="text-blue-700">{text}</span>}
        </motion.div>
    );

    return (
        <AnimatePresence mode="popLayout">
            {isLoading && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className={cn(
                        'absolute inset-0 flex h-full w-full items-start justify-center gap-2 py-24',
                        className,
                    )}
                >
                    {loadingIndicator}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LoadingOverlay;
